import React, { useState, useEffect, useRef } from 'react';
import CommonModal from '../../components/commonComponent/CommonModal';
import CommonTable from '../../components/commonComponent/CommanTable';
import UserFilters from '../../components/commonComponent/CommanFilters';
import { validateForm } from '../../utils/formUtils';
import { FaPencilAlt } from 'react-icons/fa';
import axios from '../../utils/axiosInstance';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import { hasPermission } from '../../utils/permissionHelper';

const allFields = [
  { key: 'srNo', label: 'Sr.No.' },
  { key: 'planName', label: 'Plan Name' },
  { key: 'price', label: 'Price' },
  { key: 'currency', label: 'Currency' },
  { key: 'duration', label: 'Duration (Days)' },
  { key: 'userCount', label: 'User Count' },
  { key: 'gstPercentage', label: 'GST (%)' },
  { key: 'discount', label: 'Discount (%)' },
];

const subscriptionPlanFields = [
  { type: "heading", label: "Plan Information" },
  { name: "name", label: "Plan Name", type: "text", placeholder: "Enter plan name" },
  { name: "price", label: "Price", type: "text", placeholder: "Enter price" },
  { name: "currency", label: "Currency", type: "select", options: [], placeholder: "Select Currency" },
  { name: "duration", label: "Duration (Days)", type: "text", placeholder: "Enter duration in days" },
  { name: "userCount", label: "User Count", type: "text", placeholder: "Enter user count", min: 5 },
  { type: "heading", label: "Pricing Details" },
  { name: "gstPercentage", label: "GST (%)", type: "text", placeholder: "Enter GST percentage" },
  { name: "discount", label: "Discount (%)", type: "text", placeholder: "Enter discount percentage" },
  { type: "heading", label: "Additional Information" },
  { name: "notes", label: "Notes", type: "textarea", placeholder: "Enter additional notes" },
];

const initialSubscriptionPlanValues = {
  name: "",
  price: "",
  currency: "",
  duration: "",
  userCount: "",
  gstPercentage: "",
  discount: "",
  notes: "",
};

function SubscriptionIndex() {
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(initialSubscriptionPlanValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [plans, setPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const defaultLimit = process.env.REACT_APP_PAGE_LIMIT ? parseInt(process.env.REACT_APP_PAGE_LIMIT, 10) : 10;
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);

  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const columnDropdownRef = useRef(null);

  const [user, setUser] = useState(null);
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) setUser(JSON.parse(storedUser));
    } catch {}
  }, []);

  // Permissions
  const canListPlan = hasPermission(user, 'Subscription Plan', 'List');
  const canViewPlan = hasPermission(user, 'Subscription Plan', 'View');
  const canCreatePlan = hasPermission(user, 'Subscription Plan', 'Create');
  const canEditPlan = hasPermission(user, 'Subscription Plan', 'Edit');

  const forcedFields = ['srNo'];

  const getInitialVisibleFields = () => {
    const stored = localStorage.getItem('subscription_visible_columns');
    return stored ? JSON.parse(stored) : allFields.map(f => f.key);
  };
  const [visibleFields, setVisibleFields] = useState(getInitialVisibleFields);

  useEffect(() => {
    localStorage.setItem('subscription_visible_columns', JSON.stringify(visibleFields));
  }, [visibleFields]);

  useEffect(() => {
    if (!showColumnDropdown) return;
    function handleClickOutside(event) {
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target)) {
        setShowColumnDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnDropdown]);

  const [filters, setFilters] = useState({
    countryId: '',
    stateId: '',
    cityId: '',
    startDate: '',
    endDate: '',
  });

  const [dropdowns] = useState({
    countries: [
      { value: 'in', label: 'India' },
      { value: 'us', label: 'United States' },
      { value: 'uk', label: 'United Kingdom' },
    ],
    states: [
      { value: 'gj', label: 'Gujarat' },
      { value: 'mh', label: 'Maharashtra' },
      { value: 'ka', label: 'Karnataka' },
      { value: 'tn', label: 'Tamil Nadu' },
    ],
    cities: [
      { value: 'ahd', label: 'Ahmedabad' },
      { value: 'sur', label: 'Surat' },
      { value: 'mum', label: 'Mumbai' },
      { value: 'blr', label: 'Bangalore' },
    ],
    plans: [
      { value: 'basic', label: 'Basic Plan' },
      { value: 'standard', label: 'Standard Plan' },
      { value: 'premium', label: 'Premium Plan' },
      { value: 'enterprise', label: 'Enterprise Plan' },
    ],
  });

  useEffect(() => {
    fetchPlans();
    fetchCurrencies();
  }, [canListPlan, canViewPlan]);

  const fetchPlans = async () => {
    if (!canListPlan && !canViewPlan) {
      setPlans([]);
      setFilteredPlans([]);
      return;
    }
    try {
      const response = await axios.get('/api/plan');
      if (response.data && response.data.Status === 1) {
        setPlans(response.data.plans || []);
        setFilteredPlans(response.data.plans || []);
      } else {
        toastError('Failed to fetch plans');
      }
    } catch (error) {
      toastError('Failed to fetch plans');
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get('/admin/api/currency');
      if (response.data && response.data.Status === 1) {
        const currencyOptions = response.data.currencies.map(currency => ({
          value: currency._id,
          label: `${currency.name} (${currency.symbol})`
        }));
        setCurrencies(currencyOptions);
      }
    } catch (error) {
      setCurrencies([
        { value: "68ff397490230f7b9fd29d93", label: "Indian Rupee (₹)" },
        { value: "USD", label: "US Dollar ($)" },
        { value: "EUR", label: "Euro (€)" },
        { value: "GBP", label: "British Pound (£)" },
      ]);
    }
  };

  const dynamicSubscriptionPlanFields = subscriptionPlanFields.map(field => {
    if (field.name === "currency") {
      return { ...field, options: currencies };
    }
    return field;
  });

  useEffect(() => {
    applyFilters(plans);
  }, [searchTerm, filters, plans]);

  const tableColumns = allFields
    .filter(field => forcedFields.includes(field.key) || visibleFields.includes(field.key))
    .map(field => ({ key: field.key, label: field.label }))
    .concat([{ key: 'actions', label: 'Action' }]);

  const tableData = filteredPlans.map((plan, idx) => ({
    ...plan,
    srNo: idx + 1,
    planName: plan.name,
    currency: typeof plan.currency === 'object' && plan.currency ?
      `${plan.currency.name} (${plan.currency.symbol})` :
      (plan.currency || 'Not Set'),
    default: plan.default ? (
      <span className="badge bg-success">Yes</span>
    ) : (
      <span className="badge bg-secondary">No</span>
    ),
    actions: (
      <div>
        {canEditPlan && (
          <button
            className="btn btn-sm me-1 py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
            title="Edit"
            onClick={() => handleEdit(plan)}
          >
            <FaPencilAlt size={14} />
          </button>
        )}
      </div>
    ),
  }));

  const handleAdd = () => {
    if (!canCreatePlan) {
      toastError("You do not have permission to create a plan.");
      return;
    }
    setModalData(initialSubscriptionPlanValues);
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = (plan) => {
    if (!canEditPlan) {
      toastError("You do not have permission to edit a plan.");
      return;
    }
    setModalData({
      ...plan,
      name: plan.name,
      currency: typeof plan.currency === 'object' && plan.currency ? plan.currency._id : (plan.currency || ""),
    });
    setErrors({});
    setShowModal(true);
  };

  const validationRules = {
    name: { required: true },
    price: { required: true },
    currency: { required: true },
    duration: { required: true },
    userCount: { required: true },
    gstPercentage: { required: true },
    discount: { required: false },
    notes: { required: false },
  };

  const hasInvalidUserCount = () => {
    const inputUserCount = Number(modalData.userCount);
    if (inputUserCount < 5) {
      toastError("User count must be at least 5.");
      setErrors({ userCount: "User count must be at least 5." });
      return true;
    }
    return false;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm(modalData, validationRules);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toastError("Please fill all required fields correctly.");
      return;
    }
    if (!modalData.currency) {
      setErrors({ currency: "Please select a currency" });
      toastError("Please select a currency");
      return;
    }
    if (hasInvalidUserCount()) {
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        name: modalData.name.trim(),
        price: Number(modalData.price),
        currency: modalData.currency,
        duration: Number(modalData.duration),
        userCount: Number(modalData.userCount),
        gstPercentage: Number(modalData.gstPercentage),
        discount: Number(modalData.discount || 0),
        notes: modalData.notes?.trim() || "",
      };
      let response;
      if (modalData._id) {
        response = await axios.put(`/api/plan/${modalData._id}`, payload);
      } else {
        response = await axios.post('/api/plan', payload);
      }
      if (response.data && response.data.Status === 1) {
        toastSuccess(modalData._id ? 'Plan updated successfully!' : 'Plan created successfully!');
        await fetchPlans();
        setShowModal(false);
        setModalData(initialSubscriptionPlanValues);
        setErrors({});
      } else {
        toastError(response.data.Message || response.data.error || 'Failed to save plan');
        setErrors({ api: response.data.Message || response.data.error });
      }
    } catch (error) {
      toastError('Failed to save plan. Please try again.');
      setErrors({ api: 'Failed to save plan.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    applyFilters(plans, newFilters);
  };

  const handleClearFilters = () => {
    setFilters({ countryId: '', stateId: '', cityId: '', startDate: '', endDate: '' });
    setFilteredPlans(plans);
  };

  const applyFilters = (dataToFilter = plans, filtersToApply = filters) => {
    let filtered = [...dataToFilter];
    if (searchTerm) {
      filtered = filtered.filter(plan =>
        (plan.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (plan.notes || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (typeof plan.currency === 'object' && plan.currency ? plan.currency.name.toLowerCase().includes(searchTerm.toLowerCase()) : false)
      );
    }
    if (filtersToApply.startDate) filtered = filtered.filter(plan => plan.createdAt >= filtersToApply.startDate);
    if (filtersToApply.endDate) filtered = filtered.filter(plan => plan.createdAt <= filtersToApply.endDate);
    setFilteredPlans(filtered);
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4 mt-4">
        <h2 className="h2 mb-0 text-dark">Subscripton Plan</h2>
        <div className="d-flex gap-2 align-items-center">
          {canCreatePlan && (
            <button className="btn btn-primary" onClick={handleAdd}>
              <i className="fas fa-plus me-1"></i> Create New Plan
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="mb-4">
          <UserFilters
            filters={filters}
            dropdowns={dropdowns}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
          />
        </div>
      )}

      <div className="row mb-4">
        <div className="col-md-6"></div>
        <div className="col-md-6 d-flex justify-content-end">
          <div className="input-group" style={{ maxWidth: 280, borderRadius: '6px', overflow: 'hidden' }}>
            <span className="input-group-text bg-white border-end-0" style={{ borderRadius: '6px 0 0 6px' }}>
              <i className="fas fa-search text-muted"></i>
            </span>
            <input
              type="text"
              className="form-control border-start-0"
              placeholder="Type to search..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ boxShadow: 'none', borderRadius: '0 6px 6px 0' }}
            />
          </div>
        </div>
      </div>

      <CommonTable
        columns={tableColumns}
        data={tableData}
        itemsPerPageOptions={[10, 15, 25, 50]}
        defaultItemsPerPage={10}
        page={page}
        onPageChange={setPage}
        itemsPerPage={limit}
        onItemsPerPageChange={setLimit}
      />

      <CommonModal
        visible={showModal}
        title={<span style={{ color: "#3F3F46", fontFamily: "Poppins" }}>{modalData && modalData._id ? "Edit Subscription Plan" : "New Subscription Plan"}</span>}
        fields={dynamicSubscriptionPlanFields}
        values={modalData}
        onChange={setModalData}
        onCancel={() => {
          setShowModal(false);
          setModalData(initialSubscriptionPlanValues);
          setErrors({});
        }}
        onSubmit={handleSubmit}
        submitLabel={isSubmitting ? (modalData && modalData._id ? "Updating..." : "Saving...") : (modalData && modalData._id ? "Update" : "Save")}
        cancelLabel="Cancel"
        requiredFields={["name","price","currency","duration","userCount","gstPercentage"]}
        errors={errors}
        disabled={isSubmitting}
      />
    </div>
  );
}

export default SubscriptionIndex;